import { useState, useMemo, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { submitLead, type LeadInput, type LeadSource } from "@/lib/lead";
import { markFormFocus } from "@/lib/tracking";
import {
  checkAntispam,
  markAntispamSubmitted,
  antispamMessagePt,
  checkContentQuality,
  contentMessagePt,
  createHumanSignalTracker,
  looksAutomated,
} from "@/lib/antispam";
import { trackEvent, trackMetaLead } from "@/lib/analytics";
import { useT, useLocale } from "@/i18n/LocaleProvider";

interface LeadFormProps {
  source: LeadSource;
  compact?: boolean;
  submitLabel?: string;
  showCompany?: boolean;
  showMessage?: boolean;
  onSuccess?: (data: LeadInput) => void;
}

export function LeadForm({
  source,
  compact = false,
  submitLabel,
  showCompany = true,
  showMessage = true,
  onSuccess,
}: LeadFormProps) {
  const t = useT();
  const locale = useLocale();
  const [done, setDone] = useState(false);
  const formOpenedAtRef = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement>(null);
  const signals = useMemo(() => createHumanSignalTracker(), []);
  useEffect(() => () => signals.dispose(), [signals]);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(2, t.forms.vNameMin).max(120),
        email: z.string().trim().email(t.forms.vEmail).max(200),
        phone: z
          .string()
          .trim()
          .min(8, t.forms.vPhone)
          .max(25)
          .regex(/^[\d\s()+\-]+$/, t.forms.vPhoneInvalid),
        company: z.string().trim().max(200).optional().or(z.literal("")),
        message: z.string().trim().max(2000).optional().or(z.literal("")),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<LeadInput>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", phone: "", company: "", message: "" },
  });

  const onSubmit = handleSubmit(async (data) => {
    const scope = `lead-${source}`;
    if (looksAutomated()) {
      toast.error("Envio bloqueado: detectamos automação no navegador.");
      return;
    }
    const quality = checkContentQuality(data);
    if (!quality.ok) {
      toast.error(contentMessagePt(quality) ?? t.forms.error);
      return;
    }
    const spam = await checkAntispam({
      scope,
      honeypot: honeypotRef.current?.value ?? "",
      formOpenedAt: formOpenedAtRef.current,
      contentKey: `${data.name}|${data.email}|${data.phone}|${data.message ?? ""}`,
    });
    if (!spam.ok) {
      toast.error(antispamMessagePt(spam) ?? t.forms.error);
      return;
    }
    if (!signals.read().ok) {
      toast.error("Não conseguimos confirmar uma interação humana neste envio.");
      return;
    }
    try {
      await submitLead(data, source, locale);
      // Meta Pixel + CAPI — conversão real de lead (deduplicada por event_id)
      {
        const digits = (data.phone ?? "").replace(/\D/g, "");
        const [firstName, ...rest] = (data.name ?? "").trim().split(/\s+/);
        trackMetaLead(
          { content_name: `form_${source}`, content_category: source, source },
          {
            email: data.email,
            phone: digits ? `55${digits.replace(/^55/, "")}` : undefined,
            first_name: firstName || undefined,
            last_name: rest.join(" ") || undefined,
            country: "br",
          },
        );
        trackEvent("lead_submit", { source, page: typeof window !== "undefined" ? window.location.pathname : "" });
      }
      markAntispamSubmitted(scope, spam.hash);
      setDone(true);
      reset();
      formOpenedAtRef.current = Date.now();
      toast.success(t.forms.success, { description: t.forms.successDesc });
      onSuccess?.(data);
    } catch (err) {
      console.error(err);
      toast.error(t.forms.error, { description: t.forms.errorDesc });
    }
  });

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-4 size-12 text-primary-glow" />
        <h3 className="text-xl font-semibold">{t.forms.sentTitle}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{t.forms.sentBody}</p>
        <Button variant="ghost" className="mt-4" onClick={() => setDone(false)}>
          {t.forms.sendAnother}
        </Button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full rounded-lg bg-secondary/60 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary-glow focus:ring-2 focus:ring-primary/30";

  return (
    <form onSubmit={onSubmit} onFocus={() => markFormFocus(`kentro-${source}`)} className="space-y-4">
      {/* honeypot anti-spam */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={`hp-${source}`}>Do not fill</label>
        <input ref={honeypotRef} id={`hp-${source}`} name="hp_url" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <div className={compact ? "" : "grid gap-4 sm:grid-cols-2"}>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            {t.forms.name}
          </label>
          <input className={inputCls} placeholder={t.forms.namePh} {...register("name")} />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            {t.forms.phone}
          </label>
          <input
            className={inputCls}
            placeholder={t.forms.phonePh}
            inputMode="tel"
            {...register("phone")}
          />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>}
        </div>
      </div>

      <div className={compact ? "" : "grid gap-4 sm:grid-cols-2"}>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            {t.forms.email}
          </label>
          <input
            className={inputCls}
            placeholder={t.forms.emailPh}
            type="email"
            {...register("email")}
          />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        {showCompany && (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t.forms.company}
            </label>
            <input className={inputCls} placeholder={t.forms.companyPh} {...register("company")} />
          </div>
        )}
      </div>

      {showMessage && (
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            {t.forms.message}
          </label>
          <textarea
            className={inputCls}
            rows={4}
            placeholder={t.forms.messagePh}
            {...register("message")}
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full gradient-primary text-white shadow-glow hover:opacity-95"
        size="lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin" /> {t.forms.submitting}
          </>
        ) : (
          <>
            <Send /> {submitLabel ?? t.forms.submitDemo}
          </>
        )}
      </Button>
    </form>
  );
}
